import React, { useState } from 'react';
import { Button, Form, Row, Col, Container, Card, CardBody, FormGroup, Label, Input } from 'reactstrap';
import PasswordInput from '../../../common/PasswordInput';
import { useMutationRequestQuery } from '../../../../Hooks/useMutationRequestQuery';
import { changeClientPassword } from '../../../../Services/Auth';
import { toast } from 'react-toastify';
import { RootState } from '../../../../ReduxToolkit/Store';
import { useSelector } from 'react-redux';

export default function ChangePassword() {
  const [passwordFormData, setPasswordFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [passwordFormErrors, setPasswordFormErrors] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
    confirmPasswordMismatch: false
  });

  const [isPasswordVisible, setIsPasswordVisible] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  });

  const [showAllPasswords, setShowAllPasswords] = useState(false);

  // Using the mutation hook for the password change
  const userData = useSelector((state: RootState) => state.auth);
  const {
    mutate: changePassword,
    isPending: isChangePasswordPending,
  } = useMutationRequestQuery(
    'changeClientPassword',
    changeClientPassword,
    {
      onSuccess: (data: any) => {
        toast.success("Password changed successfully.");
        setPasswordFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        })
        setPasswordFormErrors({
          currentPassword: false,
          newPassword: false,
          confirmPassword: false,
          confirmPasswordMismatch: false
        })
      },
      onError: (error: any) => {
        toast.error(error?.response?.data || "Failed to change password.");
        console.error(error);
      },
    }
  );

  const handlePasswordValueChange = (name: string, value: string) => {
    setPasswordFormData(prevData => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Basic form validation
    const { currentPassword, newPassword, confirmPassword } = passwordFormData;

    const isNewPasswordValid = newPassword === confirmPassword && newPassword.length >= 6;
    const isConfirmPasswordMismatch = newPassword !== confirmPassword;

    if (!currentPassword || !newPassword || !isNewPasswordValid) {
      setPasswordFormErrors({
        currentPassword: !currentPassword,
        newPassword: !newPassword,
        confirmPassword: !isNewPasswordValid || isConfirmPasswordMismatch,
        confirmPasswordMismatch: isConfirmPasswordMismatch,
      });
      return;
    }

    // Trigger the mutation to change the password
    changePassword({
      clientGuid: userData?.jti,
      oldPassword: passwordFormData.currentPassword,
      newPassword: passwordFormData.newPassword
    });
  };

  const toggleShowAllPasswords = () => {
    setShowAllPasswords(!showAllPasswords);
    setIsPasswordVisible({
      currentPassword: !showAllPasswords,
      newPassword: !showAllPasswords,
      confirmPassword: !showAllPasswords,
    });
  };

  const handleErrorChange = (name: string, error: boolean) => {
    setPasswordFormErrors((prevErrors: any) => ({
      ...prevErrors,
      [name]: error,
    }));
  };

  return (
    <Container fluid className="d-flex align-items-center justify-content-center" style={{ height: '60vh' }}>
      <Row
        className="d-flex shadow-sm rounded p-4 bg-white"
        style={{
          width: '100%',
          maxWidth: '1200px',
          height: '300px',
        }}
      >
        <Col xs={12} className="d-flex flex-column flex-grow-1 p-4">
          <Card>
            <CardBody>
              <Form onSubmit={handleSubmit}>
                <Row>
                  {/* Current Password */}
                  <PasswordInput
                    label="Current Password"
                    name="currentPassword"
                    value={passwordFormData.currentPassword}
                    setValue={handlePasswordValueChange}
                    placeholder="Current Password"
                    errorMessage="Current password is required"
                    error={passwordFormErrors.currentPassword}
                    showPassword={isPasswordVisible.currentPassword}
                    maxChars={20}
                    setError={handleErrorChange}
                    design="flex"
                    passCol="col-8"
                  />

                  {/* New Password */}
                  <PasswordInput
                    label="New Password"
                    name="newPassword"
                    value={passwordFormData.newPassword}
                    setValue={handlePasswordValueChange}
                    placeholder="New Password"
                    errorMessage="New password is required"
                    error={passwordFormErrors.newPassword}
                    showPassword={isPasswordVisible.newPassword}
                    maxChars={20}
                    setError={handleErrorChange}
                    design="flex"
                    passCol="col-8"
                  />

                  {/* Confirm New Password */}
                  <PasswordInput
                    label="Confirm New Password"
                    name="confirmPassword"
                    value={passwordFormData.confirmPassword}
                    setValue={handlePasswordValueChange}
                    placeholder="Confirm New Password"
                    setError={handleErrorChange}
                    errorMessage={
                      passwordFormErrors.confirmPasswordMismatch
                        ? "Passwords do not match"
                        : "Password confirmation is required"
                    }
                    error={passwordFormErrors.confirmPassword}
                    showPassword={isPasswordVisible.confirmPassword}
                    maxChars={20}
                    design="flex"
                    passCol="col-8"
                  />

                  {/* Submit and Show Passwords Checkbox */}
                  <Row xs={12}>
                    <Col md={3}>
                      <Button color="primary" type="submit" disabled={isChangePasswordPending}>
                        {isChangePasswordPending ? 'Loading . . .' : 'Change Password'}
                      </Button>
                    </Col>

                    {/* Checkbox to toggle visibility of passwords */}
                    <Col>
                      <FormGroup check>
                        <Input
                          type="checkbox"
                          id="showAllPasswords"
                          checked={showAllPasswords}
                          onChange={toggleShowAllPasswords}
                        />
                        <Label check for="showAllPasswords">
                          Show Passwords
                        </Label>
                      </FormGroup>
                    </Col>
                  </Row>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
